import { useRef, useState } from 'react';
import { Alert, Button } from 'src/components/ui';
import { Trans } from '@lingui/react/macro';
import { FileStage } from './types';
import { ValidationIndicator } from './StatusBadge';
import Icon from 'src/components/Icon';

interface FinancialDropZoneProps {
  stage: FileStage;
  onFileSelected: (file: File) => void;
  onValidate: () => void;
  /** Retry with a freshly picked file — see the Retry button for why it never re-sends the old one. */
  onRetry: (file: File) => void;
  onReplace: () => void;
  isMobile?: boolean;
}

export function FinancialDropZone({ stage, onFileSelected, onValidate, onRetry, onReplace, isMobile }: FinancialDropZoneProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const retryInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) onFileSelected(file);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) onFileSelected(file);
    e.target.value = '';
  };

  const handleRetryInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) onRetry(file);
    e.target.value = '';
  };

  if (stage.file) {
    const displayName = stage.preloadedFileName ?? stage.file.name;
    const isPreloaded = !!stage.preloadedFileName;
    return (
      <Alert color="primary" className="bulk-file-loaded p-3 mb-3 text-center" fade={false}>
        <div className="flex items-center justify-center gap-2 mt-3 mb-3" style={{ overflow: 'hidden' }}>
          <Icon icon="fa-file-excel fa-2x" className="text-primary" style={{ flexShrink: 0 }} />
          <strong style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>{displayName}</strong>
        </div>
        {stage.uploadedAt && (
          <div className="text-muted mb-2" style={{ fontSize: '0.75rem' }}>
            <Trans>Last updated:</Trans> {new Date(stage.uploadedAt).toLocaleString()}
          </div>
        )}
        <div className="flex items-center justify-center flex-wrap gap-2 mb-2">
          {stage.validationState === 'idle' && !isPreloaded && (
            <Button color="info" size="sm" style={{ fontSize: '0.78rem' }} onClick={onValidate}>
              <Trans>Validate and Submit</Trans>
            </Button>
          )}
          {stage.validationState === 'error' && (
            <>
              {/* Retry opens the picker instead of re-sending stage.file. A selected File is a snapshot:
                  once the filer fixes the workbook on disk, the browser must refuse to read it (File API
                  "snapshot state") — Firefox instead sent a malformed request. Picking the file again is
                  the only way to upload the corrected version. */}
              <input ref={retryInputRef} type="file" accept=".xlsx" style={{ display: 'none' }} onChange={handleRetryInputChange} />
              <Button color="warning" size="sm" style={{ fontSize: '0.78rem' }} onClick={() => retryInputRef.current?.click()}>
                <Trans>Retry Validation</Trans>
              </Button>
            </>
          )}
          <Button color="secondary" size="sm" outline style={{ fontSize: '0.78rem' }} onClick={onReplace}>
            ↩ <Trans>Replace</Trans>
          </Button>
        </div>
        {stage.validationState !== 'idle' && (
          <div className="flex justify-center flex-wrap">
            <ValidationIndicator stage={stage} />
          </div>
        )}
      </Alert>
    );
  }

  return (
    <>
      <input ref={inputRef} type="file" accept=".xlsx" style={{ display: 'none' }} onChange={handleInputChange} />
      <div
        className={`bulk-drop-zone${isMobile ? ' py-5' : ''} px-4 text-center mb-3${isDragging ? ' is-dragging' : ''}`}
        style={{ height: isMobile ? 'auto' : '200px' }}
        onClick={() => inputRef.current?.click()}
        onDragOver={e => {
          e.preventDefault();
          setIsDragging(true);
        }}
        onDragEnter={e => {
          e.preventDefault();
          setIsDragging(true);
        }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={handleDrop}
      >
        <span className="text-muted flex items-center flex-wrap gap-2 justify-center">
          <span className="inline-flex items-center gap-2 nowrap">
            <Icon icon="fa-upload" className="text-primary fa-lg" />
            <span>
              <Trans>Drag &amp; drop Excel file (.xlsx) here</Trans>
            </span>
          </span>
          <span className="text-primary font-bold nowrap" style={{ cursor: 'pointer' }}>
            <Trans>or browse files</Trans>
          </span>
        </span>
      </div>
    </>
  );
}
